import { AlertTriangle, Loader2, Upload } from 'lucide-react'
import { useRef, type ReactNode } from 'react'
import type { UploadState } from '../../lib/uploads'
import { Button, type ButtonProps } from './button'
import { ProgressBar } from './progress-bar'

/*
 * The pieces of a field that uploads a file (ADR 0009 §6): a button that opens the file picker,
 * and where its upload stands, sent so far (with «Cancelar»), being analysed (the malware scan), or
 * refused with why. The texts are the surface's (the editor's, the back office's), already
 * translated.
 */

/** A button that opens the file picker for one file of the types `accept` names. */
export function FileButton({
  accept,
  onFile,
  children,
  ...props
}: Omit<ButtonProps, 'onClick' | 'children'> & {
  accept: string
  onFile: (file: File) => void
  children: ReactNode
}) {
  const input = useRef<HTMLInputElement>(null)
  return (
    <>
      <Button {...props} onClick={() => input.current?.click()}>
        <Upload aria-hidden className="size-4" />
        {children}
      </Button>
      <input
        ref={input}
        type="file"
        accept={accept}
        hidden
        tabIndex={-1}
        onChange={(e) => {
          const file = e.target.files?.[0]
          // The same file chosen again is a change too.
          e.target.value = ''
          if (file) onFile(file)
        }}
      />
    </>
  )
}

export interface UploadTexts {
  /** «Subiendo …». */
  sending: (fileName: string) => string
  /** The share sent: «40 %». */
  percent: (percent: number) => string
  /** The progress bar's name. */
  progress: string
  /** «Analizando …»: the malware scan has not answered yet. */
  scanning: (fileName: string) => string
  cancel: string
  /** «Cancelar»'s full name, with the file's. */
  cancelLabel: (fileName: string) => string
  /** Why it was refused or did not finish. */
  failed: (reason: string) => string
}

/**
 * Where an upload stands; nothing once it is done. Only its phase is read out as it changes (the
 * share sent is the progress bar's).
 */
export function UploadStatus({
  state,
  texts,
  onCancel,
  field,
}: {
  state: UploadState | undefined
  texts: UploadTexts
  onCancel: () => void
  /** The field's id, for the editor to take the focus to it. */
  field?: string
}) {
  if (!state) return null
  if (state.status === 'failed')
    return (
      <p role="alert" data-field={field} className="flex items-start gap-2 text-sm text-danger">
        <AlertTriangle aria-hidden className="mt-0.5 size-4 shrink-0" />
        <span>{texts.failed(state.reason)}</span>
      </p>
    )
  return (
    <div className="space-y-2 rounded-lg border border-line p-3 text-sm" data-field={field}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p role="status" className="flex min-w-0 items-center gap-2 [overflow-wrap:anywhere]">
          {state.status === 'scanning' && (
            <Loader2 aria-hidden className="size-4 shrink-0 animate-spin text-ink-muted" />
          )}
          {state.status === 'sending'
            ? texts.sending(state.fileName)
            : texts.scanning(state.fileName)}
        </p>
        <Button
          variant="secondary"
          size="sm"
          aria-label={texts.cancelLabel(state.fileName)}
          onClick={onCancel}
        >
          {texts.cancel}
        </Button>
      </div>
      {state.status === 'sending' && (
        <div className="flex items-center gap-3">
          <ProgressBar percent={state.percent} label={texts.progress} />
          <span className="shrink-0 text-xs text-ink-muted tabular-nums">
            {texts.percent(state.percent)}
          </span>
        </div>
      )}
    </div>
  )
}
